import React, { useState, useEffect, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { completeGoogleProfile, logoutUser } from '../../services/authService';
import * as firestoreService from '../../services/firestoreService';
import SecurityUtils from '../../utils/security';
import GooglePlacesAutocomplete from '../common/GooglePlacesAutocomplete';
import DateInput from '../common/DateInput';

const ProfileCompletionScreen = ({ googleData }) => {
  const { users, showAlert, setCurrentUser, effectiveTheme } = useContext(AppContext);
  const [allUsers, setAllUsers] = useState(users || []);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(() => {
    const nameParts = (googleData.name || '').trim().split(' ');
    return {
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      handle: '',
      birthDate: '',
      gender: '',
      bio: '',
      skillLevel: 'Beginner',
      handicap: '',
      location: ''
    };
  });
  const [selectedLocationData, setSelectedLocationData] = useState(null);
  const [errors, setErrors] = useState({});

  // Load users list for handle uniqueness check if not already loaded
  useEffect(() => {
    if (allUsers.length === 0) {
      firestoreService.getUsers().then(setAllUsers).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

    if (!formData.handle.trim()) {
      newErrors.handle = 'Handle is required';
    } else if (allUsers.some(u => u.handle.toLowerCase() === formData.handle.toLowerCase())) {
      newErrors.handle = 'This handle is already taken';
    }

    if (!formData.birthDate) {
      newErrors.birthDate = 'Birth date is required';
    } else {
      const birthDate = new Date(formData.birthDate);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;
      if (actualAge < 13) newErrors.birthDate = 'You must be at least 13 years old';
    }

    if (!formData.location.trim()) newErrors.location = 'Location is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleComplete = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
      const profileData = {
        name: SecurityUtils.sanitizeText(fullName),
        firstName: SecurityUtils.sanitizeText(formData.firstName.trim()),
        lastName: SecurityUtils.sanitizeText(formData.lastName.trim()),
        handle: SecurityUtils.sanitizeText(formData.handle.trim().toLowerCase()),
        email: googleData.email.toLowerCase(),
        skillLevel: formData.skillLevel,
        handicap: formData.handicap ? parseInt(formData.handicap) : 0,
        location: {
          address: SecurityUtils.sanitizeText(formData.location.trim()),
          lat: selectedLocationData?.lat || null,
          lon: selectedLocationData?.lon || null,
          zipCode: selectedLocationData?.zipCode || '',
        },
        bio: SecurityUtils.sanitizeText(formData.bio.trim() || ''),
        birthDate: formData.birthDate,
        gender: formData.gender || '',
        profilePicture: `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=16a34a&color=fff&size=128`,
      };
      const userData = await completeGoogleProfile(googleData.id, profileData);
      setCurrentUser(userData);
    } catch (err) {
      await showAlert('Failed to save profile: ' + err.message, 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await logoutUser();
    setCurrentUser(null);
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-gray-900 to-black overflow-y-auto">
      <div className="max-w-2xl w-full bg-black rounded-lg shadow-xl border border-gray-600 p-8 my-8 mx-auto" style={{ marginBottom: '2rem' }}>
        <div className="text-center mb-8">
          <img
            src={effectiveTheme === 'dark' ? '/logos/logo-loft-dark-theme-582x243.png' : '/logos/logo-loft-light-theme-582x243.png'}
            alt="Loft"
            className="mx-auto mb-4 w-32 object-contain"
          />
          <p className="text-white mt-2">Just a few more details to get started</p>
        </div>

        <div className="space-y-4 px-2">
          {/* Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-white mb-1">First Name *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              />
              {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">Last Name *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              />
              {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
            </div>
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Email</label>
            <input
              type="email"
              value={googleData.email}
              disabled
              className="w-full px-2 py-1 border border-gray-700 rounded-lg text-gray-400 bg-gray-900 cursor-not-allowed"
            />
          </div>

          {/* Handle */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Username/Handle *</label>
            <input
              type="text"
              value={formData.handle}
              onChange={(e) => handleChange('handle', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              placeholder="johndoe"
            />
            {errors.handle && <p className="text-red-500 text-xs mt-1">{errors.handle}</p>}
          </div>

          {/* Birth Date */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Birth Date *</label>
            <DateInput
              value={formData.birthDate}
              onChange={(v) => handleChange('birthDate', v)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
            />
            {errors.birthDate && <p className="text-red-500 text-xs mt-1">{errors.birthDate}</p>}
          </div>

          {/* Gender */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Gender</label>
            <select
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
            >
              <option value="">Prefer not to say</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Non-binary">Non-binary</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => handleChange('bio', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black resize-none"
              rows="3"
              placeholder="Tell us about yourself..."
            />
          </div>

          {/* Skill Level */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Skill Level *</label>
            <select
              value={formData.skillLevel}
              onChange={(e) => handleChange('skillLevel', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
            >
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
            </select>
          </div>

          {/* Handicap */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Handicap</label>
            <input
              type="number"
              value={formData.handicap}
              onChange={(e) => handleChange('handicap', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              placeholder="18"
              min="0"
              max="54"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Address *</label>
            <GooglePlacesAutocomplete
              value={formData.location}
              onChange={(value) => handleChange('location', value)}
              onSelect={(locData) => setSelectedLocationData(locData)}
              placeholder="Start typing your address..."
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
            />
            <p className="text-xs text-gray-400 mt-1">The address is only visible to you. Users will only see the zip code. You must enter an address to find other golfers near you.</p>
            {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <button
            onClick={handleComplete}
            disabled={saving}
            className="w-full bg-[#16a34a] text-pure-white py-3 rounded-lg font-medium hover:bg-[#15803d] disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Complete Profile'}
          </button>
          <button
            onClick={handleSignOut}
            className="w-full bg-gray-800 text-white py-3 rounded-lg font-medium hover:bg-gray-700"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionScreen;
